import { computed } from 'vue';
import store from '@/store';
interface PermissionRequirements {
    rbacRoles?: string[];
    ownerRoles?: string[];
    actionPermissions?: string[];
    viewPermissions?: string[];
}
export function usePermissions() {
    const userPermissions = computed(() => store.state.userVaultnPermissions || []);
    const ownerRole = computed(() => store.state.ownerRole);
    /**
     * Check if user has required permissions
     * @param permissions - Permission requirements object
     * @returns true if user has all required permissions
     */
    function hasPermission(permissions?: PermissionRequirements | string | string[]): boolean {
        if (!permissions) return true;

        // Handle legacy string/array format as viewPermissions
        if (typeof permissions === 'string' || Array.isArray(permissions)) {
            const viewPerms = Array.isArray(permissions) ? permissions : [permissions];
            return !viewPerms || viewPerms.length === 0 || viewPerms.some(perm => userPermissions.value.includes(perm));
        }

        // Check RBAC roles (at least one required role)
        const hasRbacRole = !permissions.rbacRoles ||
            permissions.rbacRoles.length === 0 ||
            permissions.rbacRoles.some(role =>
                userPermissions.value.includes(role)
            );

        // Check owner role (must match if specified)
        const hasOwnerRole = !permissions.ownerRoles ||
            permissions.ownerRoles.length === 0 ||
            permissions.ownerRoles.includes(ownerRole.value);

        // Check view permissions (at least one required)
        const hasViewPermission = !permissions.viewPermissions ||
            permissions.viewPermissions.length === 0 ||
            permissions.viewPermissions.some(perm =>
                userPermissions.value.includes(perm)
            );

        // Check action permissions (at least one required)  
        const hasActionPermission = !permissions.actionPermissions ||
            permissions.actionPermissions.length === 0 ||
            permissions.actionPermissions.some(perm =>
                userPermissions.value.includes(perm)
            );

        return hasRbacRole && hasOwnerRole && hasViewPermission && hasActionPermission;
    }
    /**
     * Check if user has any of the specified permissions
     */
    function hasAnyPermission(permissions: string[]): boolean {
        return permissions.some(perm => userPermissions.value.includes(perm));
    }
    /**
     * Check if user has all of the specified permissions
     */
    function hasAllPermissions(permissions: string[]): boolean {
        return permissions.every(perm => userPermissions.value.includes(perm));
    }
    return {
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        userPermissions,
        ownerRole
    };
}